import React from 'react';
import Box from '@mui/material/Box';
import {
  SegmentCardPOI,
  SegmentCardMeal,
  SegmentCardTravel,
} from '../segments';
import DropIndicator from './DropIndicator';
import type { EnrichedSegment } from '../../types';

interface DaySegmentsListProps {
  segments: EnrichedSegment[];
  isEditMode: boolean;
  draggedSegment: EnrichedSegment | null;
  dragOverSegment: EnrichedSegment | null;
  dropPosition: 'before' | 'after' | null;
  onDragStart: (e: React.DragEvent, segment: EnrichedSegment) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnter: (e: React.DragEvent, segment: EnrichedSegment, index: number, daySegments: EnrichedSegment[]) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, segment: EnrichedSegment) => void;
  onDeleteSegment: (segment: EnrichedSegment) => void;
  onOpenDialog: (segment: EnrichedSegment) => void;
  onMoveSegment: (segment: EnrichedSegment, direction: 'up' | 'down') => void;
}

function DaySegmentsList({
  segments,
  isEditMode,
  draggedSegment,
  dragOverSegment,
  dropPosition,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onDeleteSegment,
  onOpenDialog,
  onMoveSegment,
}: DaySegmentsListProps) {
  // Calculate POI numbers for segments
  const segmentsWithPOINumbers = segments.map((segment, index) => {
    const poiSegments = segments.filter(s => s.segment_type === 'poi_exploration');
    const poiIndex = poiSegments.findIndex(s => s === segment);
    const isPOI = poiIndex !== -1;
    const poiNumber = isPOI ? poiIndex + 1 : null;

    return { segment, index, isPOI, poiNumber };
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: { xs: '100%', md: 900 }, mx: 'auto', px: { xs: 2, sm: 2, md: 0 } }}>
      {segmentsWithPOINumbers.map(({ segment, index, poiNumber }) => {
        const isBeingDragged = draggedSegment?.id === segment.id;
        const isDropTarget = dragOverSegment?.id === segment.id;
        const shouldGrayOut = draggedSegment && !isBeingDragged;
        const showDropLineBefore = isDropTarget && dropPosition === 'before';
        const showDropLineAfter = isDropTarget && dropPosition === 'after';

        return (
          <Box key={segment.id || index} sx={{ position: 'relative' }}>
            <DropIndicator position="before" visible={showDropLineBefore} />

            {segment.isCityTravel ? (
              <SegmentCardTravel
                segment={segment}
                isEditMode={isEditMode}
                isBeingDragged={isBeingDragged}
                shouldGrayOut={shouldGrayOut}
                isFirst={index === 0}
                isLast={index === segments.length - 1}
                onMoveUp={() => onMoveSegment(segment, 'up')}
                onMoveDown={() => onMoveSegment(segment, 'down')}
                onDragStart={(e) => onDragStart(e, segment)}
                onDragEnd={onDragEnd}
                onDragOver={onDragOver}
                onDragEnter={(e) => onDragEnter(e, segment, index, segments)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, segment)}
                onDelete={() => onDeleteSegment(segment)}
              />
            ) : segment.isMeal ? (
              <SegmentCardMeal
                segment={segment}
                isEditMode={isEditMode}
                isBeingDragged={isBeingDragged}
                shouldGrayOut={shouldGrayOut}
                isFirst={index === 0}
                isLast={index === segments.length - 1}
                onMoveUp={() => onMoveSegment(segment, 'up')}
                onMoveDown={() => onMoveSegment(segment, 'down')}
                onDragStart={(e) => onDragStart(e, segment)}
                onDragEnd={onDragEnd}
                onDragOver={onDragOver}
                onDragEnter={(e) => onDragEnter(e, segment, index, segments)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, segment)}
                onDelete={() => onDeleteSegment(segment)}
              />
            ) : (
              <SegmentCardPOI
                segment={segment}
                poiNumber={poiNumber}
                isEditMode={isEditMode}
                isBeingDragged={isBeingDragged}
                shouldGrayOut={shouldGrayOut}
                isFirst={index === 0}
                isLast={index === segments.length - 1}
                onMoveUp={() => onMoveSegment(segment, 'up')}
                onMoveDown={() => onMoveSegment(segment, 'down')}
                onCardClick={() => onOpenDialog(segment)}
                onDragStart={(e) => onDragStart(e, segment)}
                onDragEnd={onDragEnd}
                onDragOver={onDragOver}
                onDragEnter={(e) => onDragEnter(e, segment, index, segments)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, segment)}
                onDelete={() => onDeleteSegment(segment)}
              />
            )}

            <DropIndicator position="after" visible={showDropLineAfter} />
          </Box>
        );
      })}
    </Box>
  );
}

export default DaySegmentsList;
